import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Skull, Coins, Timer, Trophy, Swords } from "lucide-react";

const WIN_LABELS = {
  least_survivors: "Least Survivors Wins",
  most_survivors: "Most Survivors Wins",
  most_treasure: "Most Treasure Wins",
  timed_race: "Timed Race — Furthest Progress Wins",
};

function branchName(branches, id) {
  return branches.find((b) => b.id === id)?.name || "Unknown";
}

export function DungeonCrawlPanel({ raid, raidId, isMasterGm, api, formatApiErrorDetail }) {
  const [scenes, setScenes] = useState([]);
  const [colosseumPick, setColosseumPick] = useState("");
  const [timerMinutes, setTimerMinutes] = useState(30);
  const [extraMinutes, setExtraMinutes] = useState(5);
  const [busy, setBusy] = useState(false);
  const dc = raid?.dungeon_crawl;
  const branches = raid?.branches || [];

  useEffect(() => {
    if (raid?.raid_type !== "dungeon_crawl" || !isMasterGm || !raid?.master_game_id) return;
    api.get(`/games/${raid.master_game_id}/scenes`).then((r) => setScenes(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raid?.master_game_id, raid?.raid_type, isMasterGm]);

  if (raid?.raid_type !== "dungeon_crawl" || !dc) return null;

  const call = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const closeBranch = (branchId) => call(() => api.post(`/raids/${raidId}/dungeon-crawl/close-branch/${branchId}`));
  const finalize = () => call(async () => {
    await api.post(`/raids/${raidId}/dungeon-crawl/finalize`);
    toast.success("Results finalized");
  });
  const startTimer = () => call(async () => {
    await api.post(`/raids/${raidId}/dungeon-crawl/start-timer`, { minutes: timerMinutes });
    toast.success("Clock started");
  });
  const addTime = () => call(async () => {
    await api.post(`/raids/${raidId}/dungeon-crawl/add-time`, { minutes: extraMinutes });
    toast.success(`+${extraMinutes} minutes added for everyone`);
  });
  const setColosseum = () => call(async () => {
    await api.patch(`/raids/${raidId}/dungeon-crawl/colosseum-scene`, { scene_id: colosseumPick });
    toast.success("Colosseum scene set");
  });
  const declareTiebreak = (branchId) => call(async () => {
    await api.post(`/raids/${raidId}/dungeon-crawl/declare-tiebreak-winner`, { winner_branch_id: branchId });
    toast.success("Tiebreak winner declared");
  });
  const enableBbebbeg = () => call(async () => {
    await api.post(`/raids/${raidId}/dungeon-crawl/enable-bbebbeg`);
    toast.success("BBEBBEG enabled for the Colosseum");
  });

  return (
    <div className="panel-wood rounded-2xl p-5 space-y-4 lg:col-span-2" data-testid="raid-dungeon-crawl-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl flex items-center gap-2"><Skull className="w-4 h-4 text-[#D4AF37]" /> Dungeon Crawl</h2>
        <span className="text-[10px] uppercase tracking-widest text-[#f1e0c5]/40 px-2 py-0.5 rounded-full bg-black/30" data-testid="dungeon-crawl-win-condition-badge">{WIN_LABELS[dc.win_condition]}</span>
      </div>

      {dc.status === "completed" && dc.winner_branch_id && (
        <div className="flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-xl px-4 py-3" data-testid="dungeon-crawl-champion-banner">
          <Trophy className="w-5 h-5 text-[#D4AF37]" />
          <span className="text-sm">Winner: <strong>{branchName(branches, dc.winner_branch_id)}</strong></span>
        </div>
      )}

      {dc.win_condition === "timed_race" ? (
        <div className="space-y-2" data-testid="dungeon-crawl-timer-section">
          <p className="text-sm flex items-center gap-2"><Timer className="w-4 h-4 text-[#3B82F6]" /> {dc.timer_started_at ? `${dc.timer_minutes + dc.timer_extra_minutes} min clock (started)` : "Clock not started yet"}</p>
          {isMasterGm && (
            <div className="flex flex-wrap gap-2">
              <Input data-testid="dungeon-crawl-timer-minutes-input" type="number" min={1} value={timerMinutes} onChange={(e) => setTimerMinutes(parseInt(e.target.value) || 30)} className="bg-black/30 border-white/10 w-24" />
              <Button data-testid="dungeon-crawl-start-timer-button" disabled={busy} size="sm" onClick={startTimer} className="rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab]">Start Clock</Button>
              <Input data-testid="dungeon-crawl-extra-minutes-input" type="number" min={1} value={extraMinutes} onChange={(e) => setExtraMinutes(parseInt(e.target.value) || 5)} className="bg-black/30 border-white/10 w-24" />
              <Button data-testid="dungeon-crawl-add-time-button" disabled={busy} size="sm" variant="secondary" className="rounded-full" onClick={addTime}>+ Add Time for Everyone</Button>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {branches.map((b) => {
            const snap = dc.results?.[b.id];
            return (
              <div key={b.id} className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2" data-testid={`dungeon-crawl-branch-${b.id}`}>
                <div className="text-sm">
                  <span>{b.name}</span>
                  {snap && <span className="text-[10px] text-[#f1e0c5]/40 ml-2">{snap.survivors}/{snap.total_players} survived · <Coins className="inline w-3 h-3" /> {snap.treasure_gp}gp</span>}
                </div>
                {isMasterGm && !snap && (
                  <Button data-testid={`dungeon-crawl-close-branch-${b.id}`} size="sm" variant="secondary" className="rounded-full" disabled={busy} onClick={() => closeBranch(b.id)}>Close Dungeon</Button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {isMasterGm && dc.status === "in_progress" && dc.win_condition !== "timed_race" && Object.keys(dc.results || {}).length > 0 && (
        <Button data-testid="dungeon-crawl-finalize-button" disabled={busy} onClick={finalize} className="rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
          Finalize Results
        </Button>
      )}
      {isMasterGm && dc.status === "in_progress" && dc.win_condition === "timed_race" && dc.timer_started_at && (
        <Button data-testid="dungeon-crawl-finalize-button" disabled={busy} onClick={finalize} className="rounded-full bg-[#D90429] hover:bg-[#b8031f] uppercase tracking-widest text-sm">
          Finalize Results
        </Button>
      )}

      {dc.status === "tiebreaker" && (
        <div className="border-t border-white/10 pt-4 space-y-3" data-testid="dungeon-crawl-tiebreaker-section">
          <h3 className="text-sm uppercase tracking-widest text-[#f1e0c5]/40 flex items-center gap-2"><Swords className="w-3.5 h-3.5" /> Tied — Colosseum Time</h3>
          <p className="text-[11px] text-[#f1e0c5]/50">Tied: {dc.tiebreak_branch_ids.map((id) => branchName(branches, id)).join(" vs. ")}. Move the survivors into your Colosseum scene (a manual door works fine), let it play out however you like, then declare a winner.</p>
          {isMasterGm && (
            <>
              <div className="flex gap-2">
                <Select value={colosseumPick} onValueChange={setColosseumPick}>
                  <SelectTrigger data-testid="dungeon-crawl-colosseum-scene-select" className="bg-black/30 border-white/10 flex-1"><SelectValue placeholder="Pick your Colosseum scene..." /></SelectTrigger>
                  <SelectContent>
                    {scenes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button data-testid="dungeon-crawl-set-colosseum-button" disabled={!colosseumPick || busy} onClick={setColosseum} className="rounded-full bg-[#1E3A8A] hover:bg-[#2b4fab]">Set</Button>
              </div>
              {!raid.bbebbeg && (
                <Button data-testid="dungeon-crawl-enable-bbebbeg-button" size="sm" variant="secondary" className="rounded-full" disabled={busy} onClick={enableBbebbeg}>+ Add a BBEBBEG to the Colosseum</Button>
              )}
              <div className="flex flex-wrap gap-1.5">
                {dc.tiebreak_branch_ids.map((id) => (
                  <Button key={id} data-testid={`dungeon-crawl-declare-tiebreak-${id}`} size="sm" variant="secondary" className="rounded-full" disabled={busy} onClick={() => declareTiebreak(id)}>
                    Declare Winner: {branchName(branches, id)}
                  </Button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
